/**
 * Admin route wrapper for admin-only access
 * Extends ProtectedRoute with additional admin authorization check
 *
 * @module AdminRoute
 */

import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../utils/constants'

/**
 * Props for AdminRoute component
 */
interface AdminRouteProps {
  children: ReactNode
}

/**
 * Admin route component
 * Wraps admin-only pages and enforces admin authorization
 * Redirects non-admin users to home page
 * Shows loading spinner while auth is being verified
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const auth = useAuth()

  logger.debug(`AdminRoute: rendering`, {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    userRole: auth.user?.role,
    userId: auth.user?.id,
  })

  // Show loading state while checking authentication
  if (auth.isLoading) {
    logger.debug(`AdminRoute: showing loading state`)
    return (
      <div className="flex items-center justify-center min-h-screen bg-walrus-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-walrus-silver mx-auto mb-4"></div>
          <p className="text-walrus-silver">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!auth.isAuthenticated) {
    logger.warn(`AdminRoute: user not authenticated, redirecting to login`)
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  // Check if user is admin
  if (auth.user?.role !== 'admin') {
    logger.warn(`AdminRoute: user is not admin, redirecting to home`, {
      userRole: auth.user?.role,
      userId: auth.user?.id,
    })

    // TODO: Show toast notification when redirecting due to insufficient permissions
    // Could implement a toast provider and show notification here

    return <Navigate to={ROUTES.HOME} replace />
  }

  logger.debug(`AdminRoute: admin access granted, rendering children`, { userId: auth.user.id })
  return <>{children}</>
}
